import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Send, MessageSquarePlus, MessageCircle } from "lucide-react";
import { api, API, errMsg } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { btnS } from "./BpKit";
import { pageOf, suggestionsFor } from "./lucyPage";
import { ActionList } from "./LucyActions";
import "./lucy.css";

const ASK_MODULES = ["sales", "orders", "operations", "bp"];

export const lucyDay = () => new Intl.DateTimeFormat("en-US", { timeZone: "Asia/Kolkata", weekday: "short" }).format(new Date()).slice(0, 3).toLowerCase();
const istDate = () => new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" }).format(new Date());
let lookCache = null;
let lookPending = null;

export const useLucyLook = () => {
  const [look, setLook] = useState(lookCache && lookCache.date === istDate() ? lookCache : null);
  useEffect(() => {
    if (look) return undefined;
    if (!lookPending) lookPending = api.get("/lucy/look").then((r) => { lookCache = r.data; return r.data; }).catch(() => null).finally(() => { lookPending = null; });
    let live = true;
    lookPending.then((d) => { if (live && d) setLook(d); });
    return () => { live = false; };
  }, [look]);
  return look;
};

export const LucyAvatar = ({ size = "h-9 w-9", thinking = false, tid = "lucy-portrait" }) => {
  const look = useLucyLook();
  return (
    <img src={look?.image || `/lucy/${lucyDay()}.jpg`} alt={look?.festival ? `Lucy dressed for ${look.festival.name}` : "Lucy"} draggable="false" data-testid={tid}
      className={`${size} shrink-0 rounded-full object-cover bg-violet-100 lucy-face ${thinking ? "lucy-thinking" : ""}`} />
  );
};

export const FestivalGreeting = ({ tid }) => {
  const look = useLucyLook();
  if (!look?.festival) return null;
  return <p data-testid={tid} className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-sm px-2.5 py-1.5">{look.festival.greeting} <span className="text-amber-700">· Lucy</span></p>;
};

const stream = async (text, page, onDelta, onActions) => {
  const res = await fetch(`${API}/lucy/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text, page }) });
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(typeof j.detail === "string" ? j.detail : "Lucy couldn't answer just now. Try again in a minute.");
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      if (!p.startsWith("data: ")) continue;
      const ev = JSON.parse(p.slice(6));
      if (ev.delta) onDelta(ev.delta);
      if (ev.actions) onActions(ev.actions);
      if (ev.error) throw new Error(ev.error);
    }
  }
};

const Bubble = ({ m, i }) => (
  <div className={`flex gap-2 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`lucy-chat-message-${i}`}>
    {m.role !== "user" && <LucyAvatar size="h-7 w-7" thinking={!!m.live} tid={`lucy-chat-message-${i}-portrait`} />}
    <div className="max-w-[85%]">
      <div className={`rounded-sm px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-800"}`}>
        {m.text}{m.live && <span className="inline-block w-1.5 h-4 bg-violet-500 ml-0.5 align-middle animate-pulse" />}
      </div>
      {m.role === "user" && m.page && <div className="text-[10px] text-slate-400 text-right mt-0.5" data-testid={`lucy-chat-message-${i}-page`}>on {m.page}</div>}
      {m.role !== "user" && <ActionList items={m.actions} tid={`lucy-chat-message-${i}-actions`} />}
    </div>
  </div>
);

const LangSwitch = ({ lang, onPick }) => (
  <div className="inline-flex border border-slate-200 rounded-full p-0.5 text-[11px]" data-testid="lucy-lang-switch">
    {[["en", "English"], ["hinglish", "Hinglish"]].map(([k, l]) => (
      <button key={k} type="button" data-testid={`lucy-lang-${k}`} onClick={() => onPick(k)} aria-pressed={lang === k}
        className={`px-2.5 py-0.5 rounded-full transition-colors ${lang === k ? "bg-violet-600 text-white" : "text-slate-600 hover:text-violet-700"}`}>{l}</button>
    ))}
  </div>
);

const Chips = ({ items, onPick, disabled }) => (
  <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="lucy-chat-suggestions">
    {items.map((s, i) => (
      <button key={s} type="button" data-testid={`lucy-chat-suggestion-${i}`} disabled={disabled} onClick={() => onPick(s)}
        className="shrink-0 text-xs border border-violet-200 bg-violet-50/60 text-violet-800 hover:border-violet-400 hover:bg-violet-100 rounded-full px-3 py-1.5 transition-colors disabled:opacity-50">{s}</button>
    ))}
  </div>
);

const ChatHeader = ({ busy, page, lang, onLang, onFresh }) => (
  <SheetHeader className="px-4 py-3 border-b border-slate-200 text-left space-y-0">
    <div className="flex items-center gap-3 pr-8">
      <LucyAvatar size="h-12 w-12" thinking={busy} tid="lucy-chat-portrait" />
      <div className="min-w-0 flex-1">
        <SheetTitle className="text-base">Lucy</SheetTitle>
        <SheetDescription className="text-xs">Your AI wingman. She only sees what you can open in Axiom One.</SheetDescription>
      </div>
      <button type="button" data-testid="lucy-chat-new-button" onClick={onFresh} disabled={busy} className="text-slate-500 hover:text-violet-700 p-1" title="Start a new chat"><MessageSquarePlus className="h-5 w-5" /></button>
    </div>
    <div className="flex flex-wrap items-center justify-between gap-2 mt-2.5">
      <span className="inline-flex items-center gap-1.5 text-xs text-violet-800 bg-violet-50 border border-violet-100 rounded-full px-2.5 py-0.5 max-w-full truncate" data-testid="lucy-chat-looking-at">
        <span className="h-1.5 w-1.5 rounded-full bg-violet-500 shrink-0" />Looking at: {page.label}
      </span>
      <LangSwitch lang={lang} onPick={onLang} />
    </div>
    <div className="mt-2"><FestivalGreeting tid="lucy-festival-greeting" /></div>
  </SheetHeader>
);

const fallbackKind = (can) => {
  if (can("sales", "read")) return "other";
  return can("operations", "read") ? "tasks" : "orders";
};

const useLucyChat = (open, page) => {
  const [msgs, setMsgs] = useState([]);
  const [left, setLeft] = useState(null);
  const [lang, setLang] = useState("en");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    document.body.dataset.lucy = "open";
    toast.dismiss("lead-nudge");
    api.get("/lucy/chat").then((r) => { setMsgs(r.data.messages); setLeft(r.data.left); setLang(r.data.lang || "en"); }).catch((e) => toast.error(errMsg(e)));
    return () => { delete document.body.dataset.lucy; };
  }, [open]);
  const ask = async (t) => {
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: t, page: page.label }, { role: "lucy", text: "", live: true }]);
    try {
      await stream(t, page, (d) => setMsgs((m) => [...m.slice(0, -1), { ...m[m.length - 1], text: m[m.length - 1].text + d }]),
        (acts) => setMsgs((m) => [...m.slice(0, -1), { ...m[m.length - 1], actions: acts }]));
      setLeft((l) => (l == null ? l : Math.max(l - 1, 0)));
    } catch (e) {
      toast.error(e.message);
      setMsgs((m) => (m[m.length - 1].text ? m : m.slice(0, -1)));
    } finally {
      setMsgs((m) => m.map((x) => ({ ...x, live: false })));
      setBusy(false);
    }
  };
  const fresh = () => api.post("/lucy/chat/new").then((r) => { setMsgs([]); setLeft(r.data.left); }).catch((e) => toast.error(errMsg(e)));
  const pickLang = (k) => { setLang(k); api.put("/lucy/prefs", { lang: k }).catch((e) => toast.error(errMsg(e))); };
  return { msgs, left, lang, busy, ask, fresh, pickLang };
};

const EmptyHint = ({ lang, first, label }) => {
  const hi = `Hi${first ? ` ${first}` : ""}`;
  return (
    <p className="text-sm text-slate-600" data-testid="lucy-chat-empty">
      {lang === "hinglish" ? `${hi}! Main Lucy hoon. ${label} ke baare mein kuch bhi poochho.` : `${hi}, I'm Lucy. Ask me anything about ${label}.`}
    </p>
  );
};

const ChatInput = ({ text, setText, send, busy, left, lang }) => (<>
  <div className="flex gap-2">
    <textarea data-testid="lucy-chat-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={1500} rows={2} placeholder={lang === "hinglish" ? "Lucy se poochho…" : "Ask Lucy…"}
      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
      className="flex-1 resize-none border border-slate-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-violet-500" />
    <button type="button" data-testid="lucy-chat-send-button" onClick={() => send()} disabled={busy || !text.trim() || left === 0}
      className="bg-violet-600 text-white px-3 rounded-sm hover:bg-violet-700 disabled:opacity-50 transition-colors" title="Send"><Send className="h-4 w-4" /></button>
  </div>
  <p className="text-[11px] text-slate-500" data-testid="lucy-chat-left">{left == null ? "" : `${left} question${left === 1 ? "" : "s"} left today · `}Lucy can make mistakes, so check before you act.</p>
</>);

export const LucyChat = ({ open, onOpenChange }) => {
  const loc = useLocation();
  const { user, can } = useAuth();
  const page = pageOf(loc.pathname, loc.search);
  const { msgs, left, lang, busy, ask, fresh, pickLang } = useLucyChat(open, page);
  const [text, setText] = useState("");
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs]);
  const send = (q) => {
    const t = (q ?? text).trim();
    if (!t || busy) return;
    setText("");
    ask(t);
  };
  const first = (user?.name || "").split(" ")[0];
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col gap-0" data-testid="lucy-chat-panel">
        <ChatHeader busy={busy} page={page} lang={lang} onLang={pickLang} onFresh={fresh} />
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="lucy-chat-messages">
          {!msgs.length && <EmptyHint lang={lang} first={first} label={page.label} />}
          {msgs.map((m, i) => <Bubble key={`${i}-${m.role}`} m={m} i={i} />)}
          <div ref={end} />
        </div>
        <div className="border-t border-slate-200 p-3 space-y-2">
          <Chips items={suggestionsFor(page.kind, lang, user?.role_key, fallbackKind(can))} onPick={send} disabled={busy || left === 0} />
          <ChatInput text={text} setText={setText} send={send} busy={busy} left={left} lang={lang} />
        </div>
      </SheetContent>
    </Sheet>
  );
};

export const LucyOrb = () => {
  const { can } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  if (!ASK_MODULES.some((m) => can(m, "read")) || /^\/(hr|admin)(\/|$)/.test(loc.pathname)) return null;
  return (
    <>
      <button type="button" data-testid="lucy-orb-button" onClick={() => setOpen(true)} title="Ask Lucy" aria-label="Ask Lucy"
        className="h-9 shrink-0 inline-flex items-center gap-2 px-1.5 sm:pr-3 rounded-full border border-violet-200 bg-white/70 hover:border-violet-400 hover:bg-violet-50 transition-colors">
        <span className="lucy-orb-ring" aria-hidden><LucyAvatar size="h-7 w-7" tid="lucy-orb-portrait" /></span><span className="hidden sm:inline text-xs font-medium text-violet-800">Ask Lucy</span>
      </button>
      <LucyChat open={open} onOpenChange={setOpen} />
    </>
  );
};

export const AskLucy = () => {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  if (!can("sales", "read")) return null;
  return (
    <>
      <button type="button" data-testid="lucy-ask-button" onClick={() => setOpen(true)} className={`${btnS} inline-flex items-center hover:border-violet-500`}>
        <MessageCircle className="h-4 w-4 mr-1 text-violet-600" />Ask Lucy
      </button>
      <LucyChat open={open} onOpenChange={setOpen} />
    </>
  );
};

const Skeleton = () => (
  <div className="space-y-2.5" data-testid="lucy-card-loading">
    <p className="text-xs text-slate-500">Lucy is reading your numbers…</p>
    {[92, 78, 85].map((w) => <div key={w} className="h-3 bg-slate-100 rounded-sm animate-pulse" style={{ width: `${w}%` }} />)}
  </div>
);

export const LucyCard = () => {
  const { can } = useAuth();
  const [c, setC] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const ok = can("sales", "read");
  const load = (refresh) => {
    setBusy(true);
    setErr("");
    (refresh ? api.post("/lucy/card/refresh") : api.get("/lucy/card")).then((r) => setC(r.data))
      .catch((e) => (refresh && c ? toast.error(errMsg(e)) : setErr(errMsg(e)))).finally(() => setBusy(false));
  };
  useEffect(() => {
    if (!ok) return;
    setBusy(true);
    api.get("/lucy/card").then((r) => setC(r.data)).catch((e) => setErr(errMsg(e))).finally(() => setBusy(false));
  }, [ok]);
  if (!ok) return null;
  const at = c && new Date(c.at).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "numeric", minute: "2-digit" });
  return (
    <section data-testid="lucy-card" className="bg-white border border-slate-300 border-l-4 border-l-violet-500 rounded-sm ax-fade">
      <div className="flex flex-wrap items-center gap-3 px-4 pt-4 pb-3">
        <LucyAvatar size="h-11 w-11" thinking={busy} tid="lucy-card-portrait" />
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold tracking-tight text-slate-900">Lucy <span className="text-xs font-normal text-slate-500">· your AI wingman</span></h3>
          <p className="text-xs text-slate-500">Today's tips from your live plan, pipeline and follow-ups</p>
        </div>
        <div className="flex gap-2">
          {c?.can_refresh && <button type="button" data-testid="lucy-card-refresh-button" onClick={() => load(true)} disabled={busy} className={`${btnS} inline-flex items-center`} title="Lucy re-reads your numbers (3 times a day)">
            <RefreshCw className={`h-4 w-4 mr-1 ${busy ? "animate-spin" : ""}`} />Refresh</button>}
          <button type="button" data-testid="lucy-card-ask-button" onClick={() => setOpen(true)} className="bg-violet-600 text-white h-9 px-4 text-sm rounded-sm font-medium hover:bg-violet-700 transition-colors inline-flex items-center">
            <MessageCircle className="h-4 w-4 mr-1" />Ask Lucy</button>
        </div>
      </div>
      <div className="px-4 pb-4">
        <div className="mb-2"><FestivalGreeting tid="lucy-card-festival" /></div>
        {busy && !c ? <Skeleton /> : err ? (
          <p className="text-sm text-amber-700" data-testid="lucy-card-error">{err} <button type="button" className="underline ml-1" onClick={() => load(false)} data-testid="lucy-card-retry">Try again</button></p>
        ) : c && (
          <>
            <ol className="space-y-2" data-testid="lucy-card-tips">
              {c.tips.map((t, i) => (
                <li key={t} className="flex gap-3 text-sm text-slate-800 ax-fade" style={{ animationDelay: `${i * 60}ms` }} data-testid={`lucy-card-tip-${i}`}>
                  <span className="num text-xs font-bold text-violet-600 mt-0.5 w-4 shrink-0">{i + 1}</span>
                  <div className="min-w-0 flex-1"><span className="leading-relaxed">{t}</span><ActionList items={(c.actions || []).filter((a) => a.tip === i)} tid={`lucy-card-tip-${i}-actions`} /></div>
                </li>
              ))}
            </ol>
            <ActionList items={(c.actions || []).filter((a) => a.tip == null || a.tip >= c.tips.length)} tid="lucy-card-actions" />
            <p className="text-[11px] text-slate-500 mt-3" data-testid="lucy-card-time">Written at {at}. Lucy can make mistakes, so check before you act.</p>
          </>
        )}
      </div>
      <LucyChat open={open} onOpenChange={setOpen} />
    </section>
  );
};
